import { SDK_VERSION } from './constants';
import { HttpClient } from './http-client';
import { StorageManager } from './storage';
import type {
  SdkConfigResponse,
  ConsentItem,
  ConsentMap,
  ConsentStatus,
  DeviceInfo,
} from './types';

export class ConsentManager {
  private httpClient: HttpClient;
  private storage: StorageManager;
  private config: SdkConfigResponse | null = null;
  private consents: Map<string, ConsentStatus> = new Map();

  onConsentChange?: (consents: ConsentMap) => void;

  constructor(httpClient: HttpClient, storage: StorageManager) {
    this.httpClient = httpClient;
    this.storage = storage;
  }

  async initialize(): Promise<SdkConfigResponse> {
    const subjectId = this.storage.getSubjectId();
    this.config = await this.httpClient.getConfig(subjectId);

    this.consents.clear();
    for (const state of this.config.consentStates) {
      this.consents.set(state.purposeId, state.status);
    }

    return this.config;
  }

  getConfig(): SdkConfigResponse | null {
    return this.config;
  }

  async acceptAll(): Promise<void> {
    if (!this.config) return;
    const policyVersionId = this.getFirstActivePolicyVersionId();
    const items: ConsentItem[] = this.config.purposes.map((p) => ({
      purposeId: p.id,
      policyVersionId,
      action: 1 as const,
    }));
    await this.submitConsents(items);
  }

  async rejectAll(): Promise<void> {
    if (!this.config) return;
    const policyVersionId = this.getFirstActivePolicyVersionId();
    const items: ConsentItem[] = this.config.purposes.map((p) => ({
      purposeId: p.id,
      policyVersionId,
      action: 2 as const,
    }));
    await this.submitConsents(items);
  }

  async savePreferences(decisions: Map<string, boolean>): Promise<void> {
    if (!this.config) return;
    const policyVersionId = this.getFirstActivePolicyVersionId();
    const items: ConsentItem[] = Array.from(decisions.entries()).map(
      ([purposeId, granted]) => ({
        purposeId,
        policyVersionId,
        action: (granted ? 1 : 2) as 1 | 2,
      }),
    );
    await this.submitConsents(items);
  }

  getConsents(): ConsentMap {
    const result: ConsentMap = {};
    this.consents.forEach((status, purposeId) => {
      result[purposeId] = status;
    });
    return result;
  }

  getConsentsMap(): Map<string, ConsentStatus> {
    return new Map(this.consents);
  }

  async setSubjectId(newId: string): Promise<void> {
    this.storage.setSubjectId(newId);
    await this.initialize();
  }

  /** True if every purpose already has a consent decision recorded. */
  hasAllDecisions(): boolean {
    if (!this.config) return false;
    return this.config.purposes.every((p) => this.consents.has(p.id));
  }

  // ── Private ──

  private async submitConsents(items: ConsentItem[]): Promise<void> {
    if (items.length === 0) return;

    const device = this.detectDevice();

    const response = await this.httpClient.recordConsents({
      subjectId: this.storage.getSubjectId(),
      consents: items,
      sdkVersion: SDK_VERSION,
      device,
    });

    for (const state of response.consentStates) {
      this.consents.set(state.purposeId, state.status);
    }

    this.storage.setBannerDismissed();
    this.onConsentChange?.(this.getConsents());
  }

  private getFirstActivePolicyVersionId(): string {
    return this.config?.policyVersions[0]?.policyVersionId ?? '';
  }

  private detectDevice(): DeviceInfo {
    const ua = navigator.userAgent;
    return {
      deviceType: /Mobi|Android/i.test(ua) ? 'Mobile' : 'Desktop',
      operatingSystem: this.detectOS(ua),
      browser: this.detectBrowser(ua),
    };
  }

  private detectOS(ua: string): string {
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac OS')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (/iPhone|iPad|iPod/.test(ua)) return 'iOS';
    return 'Unknown';
  }

  private detectBrowser(ua: string): string {
    if (ua.includes('Firefox/')) return 'Firefox';
    if (ua.includes('Edg/')) return 'Edge';
    if (ua.includes('Chrome/')) return 'Chrome';
    if (ua.includes('Safari/')) return 'Safari';
    return 'Unknown';
  }
}
